import React from 'react';

import type { Firearm, FirearmRepair, Issuance } from '../../types/domain';
import DetailDrawer from './DetailDrawer';
import FirearmMasterImagePanel from './FirearmMasterImagePanel';
import StatusBadge from './StatusBadge';
import './FirearmIssuanceDetailsDrawer.css';

interface Props {
  issuance: Issuance;
  firearm?: Firearm;
  repair?: FirearmRepair | null;
  onClose: () => void;
  onCompleteRepair?: (repair: FirearmRepair) => void;
}

interface DetailFieldProps {
  label: string;
  value?: React.ReactNode;
  wide?: boolean;
}

const display = (value: React.ReactNode) =>
  value === null || value === undefined || value === '' ? '—' : value;

const humanize = (value?: string | null) =>
  value ? value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()) : '—';

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
};

const formatFileSize = (value?: number | null) => {
  if (!value) return '—';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const activeHandgunsAtSubmission = (issuance: Issuance) =>
  (issuance.metadata?.multiple_handgun_policy?.active_handguns_at_submission || []) as Array<{
    issuance_id?: number;
    firearm_id?: number;
    firearm_serial_number?: string;
    firearm_article?: string;
    issued_at?: string | null;
  }>;

export const DetailField: React.FC<DetailFieldProps> = ({ label, value, wide = false }) => (
  <div className={wide ? 'record-detail-field record-detail-field--wide' : 'record-detail-field'}>
    <dt>{label}</dt>
    <dd>{display(value)}</dd>
  </div>
);

const FirearmIssuanceDetailsDrawer: React.FC<Props> = ({
  issuance,
  firearm,
  repair,
  onClose,
  onCompleteRepair,
}) => (
  <DetailDrawer
    open
    title="Firearm and Issuance Details"
    subtitle={issuance.firearm_serial || firearm?.serial_number}
    className="record-details-drawer"
    meta={<StatusBadge status={issuance.status} label={issuance.status_display || humanize(issuance.status)} />}
    footer={repair?.can_complete && onCompleteRepair ? (
      <button className="btn btn-primary" type="button" onClick={() => onCompleteRepair(repair)}>
        Complete Repair
      </button>
    ) : undefined}
    onClose={onClose}
  >
    <section className="record-detail-section">
      <h3>Firearm Information</h3>
      <dl className="record-detail-grid">
        <DetailField label="Serial Number" value={issuance.firearm_serial || firearm?.serial_number} />
        <DetailField label="Article / Type" value={firearm?.article} />
        <DetailField label="Description / Make" value={firearm?.description} wide />
        <DetailField label="Model" value={firearm?.model || issuance.firearm_model_display} />
        <DetailField label="Caliber" value={firearm?.caliber} />
        <DetailField label="Department" value={firearm?.department_name} wide />
        <DetailField
          label="Current Firearm Status"
          value={issuance.current_firearm_status_display || humanize(issuance.firearm_status)}
        />
        <DetailField
          label="Current Condition"
          value={humanize(issuance.current_firearm_condition || firearm?.servicability)}
        />
      </dl>
    </section>

    <section className="record-detail-section">
      <h3>Issuance Information</h3>
      <dl className="record-detail-grid">
        <DetailField label="Issuance Reference" value={`ISS-${issuance.id}`} />
        <DetailField label="Issuance Status" value={issuance.status_display || humanize(issuance.status)} />
        <DetailField
          label="Active Assignment"
          value={(
            <StatusBadge
              status={issuance.has_active_assignment ? 'active' : 'inactive'}
              label={issuance.active_assignment_label || (issuance.has_active_assignment ? 'Active' : 'Inactive')}
              tone={issuance.has_active_assignment ? 'success' : 'neutral'}
            />
          )}
        />
        <DetailField label="Officer" value={issuance.officer_name || issuance.officer_badge} wide />
        <DetailField
          label="ARE Status"
          value={issuance.are_record?.display_status || issuance.are_record?.status || 'No ARE'}
        />
        <DetailField label="Requested Date" value={formatDate(issuance.requested_at)} />
        <DetailField label="Approved / Issued Date" value={formatDate(issuance.issued_at)} />
        <DetailField
          label="Returned / Closed Date"
          value={formatDate(issuance.return_or_closure_date || issuance.returned_at)}
        />
      </dl>
    </section>

    {issuance.review_type === 'multiple_handgun_policy' ? (
      <section className="record-detail-section">
        <h3>Additional-Handgun Authorization</h3>
        <dl className="record-detail-grid">
          <DetailField label="Review Type" value={issuance.review_type_display} />
          <DetailField label="Review Status" value={humanize(issuance.authorization_review?.review_status)} />
          <DetailField label="Authority Reference" value={issuance.authorization_review?.authority_reference} wide />
          <DetailField label="Document" value={issuance.authorization_review?.document_original_name} wide />
          <DetailField label="File Type" value={issuance.authorization_review?.document_mime_type} />
          <DetailField label="File Size" value={formatFileSize(issuance.authorization_review?.document_size)} />
          <DetailField label="Submitted By" value={issuance.authorization_review?.submitted_by_username} />
          <DetailField label="Submitted At" value={formatDate(issuance.authorization_review?.submitted_at)} />
          <DetailField label="Submission Remarks" value={issuance.authorization_review?.submission_remarks} wide />
          <DetailField label="Review Remarks" value={issuance.authorization_review?.review_remarks} wide />
        </dl>
        {activeHandgunsAtSubmission(issuance).length ? (
          <table className="records-table">
            <thead><tr><th>Issuance</th><th>Firearm</th><th>Article</th><th>Issued</th></tr></thead>
            <tbody>{activeHandgunsAtSubmission(issuance).map((item) => (
              <tr key={`${item.issuance_id}-${item.firearm_id}`}>
                <td>{item.issuance_id ? `ISS-${item.issuance_id}` : '—'}</td>
                <td>{display(item.firearm_serial_number)}</td>
                <td>{display(item.firearm_article)}</td>
                <td>{formatDate(item.issued_at)}</td>
              </tr>
            ))}</tbody>
          </table>
        ) : null}
      </section>
    ) : null}

    {repair ? (
      <section className="record-detail-section">
        <h3>Repair Information</h3>
        <dl className="record-detail-grid">
          <DetailField label="Repair Status" value={repair.status_display || humanize(repair.status)} />
          <DetailField
            label="Received Condition"
            value={repair.received_condition_display || humanize(repair.received_condition)}
          />
          <DetailField label="Reported Defect" value={repair.reported_defect} wide />
          <DetailField label="Completion Date" value={formatDate(repair.completion_date)} />
          <DetailField
            label="Post-Repair Condition"
            value={repair.post_repair_condition_display || humanize(repair.post_repair_condition)}
          />
          <DetailField label="Repair Remarks / Work Performed" value={repair.work_performed} wide />
        </dl>
        <div className="repair-evidence">
          <h4>Photo Evidence</h4>
          {repair.evidence.length ? (
            <div className="repair-photo-preview-grid">
              {repair.evidence.map((item) => (
                <a key={item.id} href={item.preview_url} target="_blank" rel="noreferrer">
                  <img src={item.preview_url} alt={`Repair evidence ${item.original_name}`} />
                  <span>{item.original_name}</span>
                </a>
              ))}
            </div>
          ) : <p className="records-table-muted">No repair photo evidence uploaded.</p>}
        </div>
      </section>
    ) : null}

    <section className="record-detail-section">
      <h3>Registry Media</h3>
      <FirearmMasterImagePanel
        image={firearm?.primary_image}
        firearmSerialNumber={issuance.firearm_serial || firearm?.serial_number}
      />
    </section>
  </DetailDrawer>
);

export default FirearmIssuanceDetailsDrawer;
